import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Chip, Avatar, Button, CircularProgress, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ApiService from '../../services/api';
import { isBoostActive, boostCardSx } from '../../utils/boost';

export default function JobSeekerDirectory() {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    ApiService.getJobSeekers({ page: 1, limit: 12 })
      .then((res) => {
        if (!mounted) return;
        const list = res.users || res.data?.users || res.jobSeekers || res.data?.jobSeekers || res.data?.data?.users || [];
        const isBoostActiveFor = (u) => isBoostActive(u?.boosted, u?.boostExpiresAt);
        const sorted = [...list].sort((a, b) => {
          const aBoost = isBoostActiveFor(a);
          const bBoost = isBoostActiveFor(b);
          if (aBoost !== bBoost) return aBoost ? -1 : 1;
          if (!!a?.featured !== !!b?.featured) return a?.featured ? -1 : 1;
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
        setJobSeekers(sorted);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || 'Failed to load job seekers');
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Job Seekers
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Browse job seekers and view their profiles
          </Typography>
        </Box>
        <Button variant="contained" component={RouterLink} to="/jobs">
          Browse Jobs
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {!loading && !error && (
        <Grid container spacing={3} alignItems="stretch">
          {jobSeekers.map((seeker) => {
            // Derive initials from first and last name; fallback to email/username
            const first = (seeker.firstName || '').trim();
            const last = (seeker.lastName || '').trim();
            const initialsBase = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
            const fallback = (seeker.email || seeker.username || '').toUpperCase();
            const initials = initialsBase || (fallback ? fallback.slice(0, 2) : 'NA');

            // Use avatar image if available, otherwise default brand image
            const avatarSrc = seeker.avatar || '/logo192.png';
            const skills = seeker.jobSeekerProfile?.skills || [];
            const headline = seeker.jobSeekerProfile?.headline || seeker.bio;
            const city = seeker.jobSeekerProfile?.location?.city || seeker.location?.city;
            const country = seeker.jobSeekerProfile?.location?.country || seeker.location?.country;
            const location = [city, country].filter(Boolean).join(', ');

            return (
              <Grid item xs={12} sm={6} md={4} key={seeker._id} sx={{ display: 'flex' }}>
                {(() => {
        const boostActive = isBoostActive(seeker?.boosted, seeker?.boostExpiresAt);
                  return (
            <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', ...(boostActive ? boostCardSx : {}) }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar src={avatarSrc} sx={{ mr: 2 }}>{initials}</Avatar>
                      <Box>
                        <Typography variant="h6">{initials}</Typography>
                        <Typography variant="body2" color="text.secondary">{location}</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {boostActive && (<Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />)}
                        </Box>
                      </Box>
                    </Box>
                    {headline && <Typography variant="body2" sx={{ mb: 1 }}>{headline}</Typography>}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {skills.slice(0, 5).map((skill) => (
                        <Chip key={skill} label={skill} size="small" />
                      ))}
                    </Box>
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to={`/job-seekers/${seeker._id}`}
                        state={{ seeker }}
                      >
                        View Profile
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
                  );})()}
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
}